import PropTypes from "prop-types";
import React, { useState } from "react";

import { classNames } from "@swa-ui/string";

import { Transition } from "../Transition";
import styles from "./SlideIntoView.module.scss";

/**
 * SlideIntoView provides a way to animate an element into view and out of view.
 */
export const SlideIntoView = (props) => {
  const { children, className, delay, onTransformationEnd, slideDirection } = props;
  const [hidden, setHidden] = useState(isHidden());

  return <Transition {...getTransitionProps()}>{children}</Transition>;

  function getTransitionProps() {
    const playList = getPlayList();

    return {
      animate: getPlayList(),
      className: getClass(),
      onTransformationEnd: handleTransformationEnd,
      transition: {
        delay: slideDirection === "hidden" ? undefined : delay,
        times: playList.opacity.length === 2 ? [0.001, 1] : undefined,
      },
    };
  }

  function getClass() {
    return classNames(className, {
      [styles.hidden]: isHidden() && hidden,
    });
  }

  function handleTransformationEnd() {
    setHidden(isHidden());
    onTransformationEnd?.();
  }

  function getPlayList() {
    const playLists = {
      default: { opacity: 1, x: "0%", y: "0%" },
      hidden: { opacity: 0, x: "0%", y: "0%" },
      hideDown: { opacity: 0, x: "0%", y: "75%" },
      hideLeft: { opacity: 0, x: "-75%", y: "0" },
      hideRight: { opacity: 0, x: "75%", y: "0%" },
      hideUp: { opacity: 0, x: "0%", y: "-75%" },
      slideDown: { opacity: [0, 1], x: ["0%", "0%"], y: ["-75%", "0%"] },
      slideLeft: { opacity: [0, 1], x: ["75%", "0%"], y: ["0%", "0%"] },
      slideRight: { opacity: [0, 1], x: ["-75%", "0%"], y: ["0%", "0%"] },
      slideUp: { opacity: [0, 1], x: ["0%", "0%"], y: ["75%", "0%"] },
    };

    return playLists[slideDirection];
  }

  function isHidden() {
    return (
      slideDirection === "hideDown" ||
      slideDirection === "hideLeft" ||
      slideDirection === "hideRight" ||
      slideDirection === "hideUp" ||
      slideDirection === "hidden"
    );
  }
};

SlideIntoView.displayName = "SlideIntoView";

SlideIntoView.propTypes = {
  /** Content to be rendered. */
  children: PropTypes.node.isRequired,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Milliseconds before slide animation begins. */
  delay: PropTypes.number,

  /** Function called once slide animation is complete. */
  onTransformationEnd: PropTypes.func,

  /**
   * Where content will be placed. Typically content will be rendered as hidden, then slide into view.
   * For example, content should be hidden initially, then "slideRight" given, then to remove from
   * page, specify "hideLeft".
   */
  slideDirection: PropTypes.oneOf([
    "default",
    "hidden",
    "hideDown",
    "hideLeft",
    "hideRight",
    "hideUp",
    "slideDown",
    "slideLeft",
    "slideRight",
    "slideUp",
  ]),
};

SlideIntoView.defaultProps = {
  slideDirection: "default",
};
